import type { RemoteConsent, RemoteConsentView } from '@api/remote/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getRemoteConsent, setRemoteConsent } from '../lib/api';
import { useScope } from '../scope/scope';
import { cn } from '../ui/cn';
import { Skeleton } from '../ui/misc';
import { useToast } from '../ui/toast';

const SECONDS: Array<[RemoteConsent['seconds'], string]> = [[15, '15 seconds'], [30, '30 seconds'], [60, '1 minute']];

/** The setting governs screen sessions only (Harry, 2026-10-21, WRMM-159). */
export const TERMINAL_NOTE = 'This is for screen sessions. A terminal never asks or notifies the person at the computer: it needs the technician’s key touch and is recorded.';

export function useRemoteConsent(organizationId: string | null | undefined) {
  const { mspId } = useScope();
  return useQuery({ queryKey: ['scope', mspId, 'remote-consent', organizationId], enabled: !!organizationId,
    queryFn: ({ signal }) => getRemoteConsent(mspId, organizationId!, signal) });
}

/** One line saying what happens when a technician connects to this customer's computers. */
export function describeConsent(c: RemoteConsent) {
  if (!c.ask) return 'Technicians are let in at once; the person at the computer sees the “WITTCO Connected” tab.';
  const wait = SECONDS.find(([s]) => s === c.seconds)?.[1] ?? `${c.seconds} seconds`;
  return c.denyUnanswered
    ? `The person at the computer is asked first and has ${wait} to allow it. No answer, or nobody there to answer, means no access.`
    : `The person at the computer is asked first and has ${wait} to answer. If nobody answers (or nobody is signed in, or the screen is locked), the technician is let in.`;
}

/** The same, in a few words for a summary line. */
export function summarizeConsent(c: RemoteConsent) {
  if (!c.ask) return 'let in at once';
  return `ask first (${c.seconds % 60 ? `${c.seconds} s` : `${c.seconds / 60} min`}), ${c.denyUnanswered ? 'no access' : 'let in'} if unanswered`;
}

/**
 * A customer's remote consent setting (WRMM-64): owners change it here, everyone else sees it. Sessions keep the setting
 * they were asked for with; changes are in the audit trail.
 */
export function ConsentSetting({ organizationId, name }: { organizationId: string; name?: string }) {
  const { mspId } = useScope();
  const consent = useRemoteConsent(organizationId);
  const client = useQueryClient();
  const toast = useToast();
  const save = useMutation({
    mutationFn: (next: RemoteConsent) => setRemoteConsent(mspId, organizationId, next),
    onSuccess: view => { client.setQueryData<RemoteConsentView>(['scope', mspId, 'remote-consent', organizationId], view); toast({ title: 'Remote session setting saved', body: describeConsent(view) }); },
    onError: () => toast({ tone: 'warning', title: 'The setting wasn’t saved', body: 'Only an owner can change it. Try again.' }),
  });
  if (consent.isPending) return <Skeleton className="h-16 w-full" />;
  if (consent.isError) return <p className="text-[13px] text-ink-3">The remote session setting couldn’t be loaded.</p>;
  const c = consent.data;
  const change = (patch: Partial<RemoteConsent>) => save.mutate({ ask: c.ask, seconds: c.seconds, denyUnanswered: c.denyUnanswered, ...patch });
  if (!c.canChange) return <p className="text-[13px] text-ink-2">{describeConsent(c)} <span className="text-ink-3">Only an owner can change this. {TERMINAL_NOTE}</span></p>;
  const disabled = save.isPending;
  return (
    <fieldset className="space-y-2 text-[13.5px]" disabled={disabled}>
      <legend className="sr-only">When a technician connects to {name ?? 'this customer'}’s computers</legend>
      <label className="flex items-start gap-2"><input type="radio" name={`consent-${organizationId}`} className="mt-1" checked={!c.ask} onChange={() => change({ ask: false })} />
        <span>Let technicians in at once <span className="block text-[12.5px] text-ink-3">The person at the computer sees the “WITTCO Connected” tab and can end the session.</span></span></label>
      <label className="flex items-start gap-2"><input type="radio" name={`consent-${organizationId}`} className="mt-1" checked={c.ask} onChange={() => change({ ask: true })} />
        <span>Ask the person at the computer first</span></label>
      <div className={cn('ml-6 space-y-2', !c.ask && 'opacity-50')}>
        <label className="flex items-center gap-2 text-[13px] text-ink-2">Time to answer
          <select aria-label="Time to answer" disabled={!c.ask || disabled} value={c.seconds} onChange={e => change({ seconds: Number(e.target.value) as RemoteConsent['seconds'] })}
            className="h-8 rounded-lg border border-line bg-panel px-2 text-[13px] outline-none focus:border-brand/50">
            {SECONDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select></label>
        <label className="flex items-start gap-2 text-[13px]"><input type="checkbox" className="mt-0.5" disabled={!c.ask || disabled} checked={c.denyUnanswered}
          onChange={e => change({ denyUnanswered: e.target.checked })} />
          <span>No access if nobody allows it in time <span className="block text-[12.5px] text-ink-3">For customers who don’t want anyone in without them there. Also applies when nobody is signed in or the screen is locked. Unticked, the technician is let in when the time is up.</span></span></label>
      </div>
      <p className="text-[12.5px] text-ink-3">{TERMINAL_NOTE}</p>
    </fieldset>
  );
}
